/**
 * @fileoverview Multi-tab synchronization with BroadcastChannel compatibility
 */

import { EventEmitter } from 'eventemitter3'

/**
 * Sync options configuration
 */
export interface SyncOptions {
  /** Sync channel name */
  channelName?: string
  /** Enable state synchronization */
  syncState?: boolean
  /** Enable file synchronization */
  syncFiles?: boolean
  /** Enable cursor/selection sync */
  syncCursor?: boolean
  /** Enable terminal sync */
  syncTerminal?: boolean
  /** Sync debounce delay in milliseconds */
  debounceMs?: number
  /** Enable conflict resolution */
  conflictResolution?: 'last-write-wins' | 'merge' | 'manual'
  /** Maximum sync payload size */
  maxPayloadSize?: number
  /** Enable compression for large payloads */
  enableCompression?: boolean
}

/**
 * Sync event types
 */
export type SyncEventType =
  | 'state-change'
  | 'file-change'
  | 'cursor-move'
  | 'selection-change'
  | 'terminal-output'
  | 'scroll-sync'
  | 'focus-change'
  | 'tab-active'
  | 'tab-inactive'
  | 'conflict'
  | 'connection'
  | 'disconnection'

/**
 * Sync message structure
 */
export interface SyncMessage {
  /** Message type */
  type: SyncEventType
  /** Message payload */
  payload: any
  /** Timestamp */
  timestamp: number
  /** Tab ID that sent the message */
  tabId: string
  /** Sandbox ID */
  sandboxId: string
  /** Message ID for deduplication */
  messageId: string
  /** Sequence number for ordering */
  sequenceNumber: number
  /** Compression flag */
  compressed?: boolean
}

/**
 * Tab information
 */
export interface TabInfo {
  /** Unique tab ID */
  id: string
  /** Tab title */
  title?: string
  /** Last activity timestamp */
  lastActivity: number
  /** Whether tab is active */
  isActive: boolean
  /** Cursor position */
  cursor?: {
    file: string
    line: number
    column: number
  }
  /** Current file being edited */
  currentFile?: string
  /** Scroll position */
  scrollPosition?: {
    x: number
    y: number
  }
}

/**
 * File change information for sync
 */
export interface FileSyncChange {
  /** File path */
  path: string
  /** Change type */
  type: 'create' | 'update' | 'delete' | 'move'
  /** File content (for create/update) */
  content?: string
  /** Previous path (for move) */
  previousPath?: string
  /** Change timestamp */
  timestamp: number
  /** Tab that made the change */
  tabId: string
  /** Change hash for conflict detection */
  hash: string
  /** Incremental changes (for efficient sync) */
  delta?: {
    start: number
    end: number
    text: string
  }[]
}

/**
 * Cursor/selection sync information
 */
export interface CursorSyncInfo {
  /** File path */
  file: string
  /** Cursor position */
  position: {
    line: number
    column: number
  }
  /** Selection range */
  selection?: {
    start: { line: number; column: number }
    end: { line: number; column: number }
  }
  /** Tab ID */
  tabId: string
  /** Tab color for visual identification */
  color?: string
  /** User info */
  user?: {
    name: string
    avatar?: string
  }
}

/**
 * Multi-tab sync manager
 */
export class MultiTabSyncManager extends EventEmitter {
  private broadcastChannel?: BroadcastChannel
  private sandboxId: string
  private tabId: string
  private options: SyncOptions
  private isEnabled = false
  private sequenceNumber = 0
  private messageDeduplication = new Set<string>()
  private connectedTabs = new Map<string, TabInfo>()
  private debounceTimers = new Map<string, NodeJS.Timeout>()
  private conflictQueue = new Map<string, FileSyncChange[]>()
  private compressionWorker?: Worker

  constructor(sandboxId: string, options: SyncOptions = {}) {
    super()
    this.sandboxId = sandboxId
    this.tabId = this.generateTabId()
    this.options = {
      channelName: `sandbox-sync-${sandboxId}`,
      syncState: true,
      syncFiles: true,
      syncCursor: true,
      syncTerminal: false, // Disabled by default for performance
      debounceMs: 100,
      conflictResolution: 'last-write-wins',
      maxPayloadSize: 1024 * 1024, // 1MB
      enableCompression: true,
      ...options
    }

    // Initialize compression worker for large payloads
    if (this.options.enableCompression && typeof Worker !== 'undefined') {
      this.initializeCompressionWorker()
    }
  }

  /**
   * Enable multi-tab sync
   */
  async enable(): Promise<void> {
    if (this.isEnabled) {
      return
    }

    if (typeof BroadcastChannel === 'undefined') {
      throw new Error('BroadcastChannel is not supported in this environment')
    }

    try {
      this.broadcastChannel = new BroadcastChannel(this.options.channelName!)
      this.setupMessageHandlers()
      this.isEnabled = true

      // Announce this tab's presence
      await this.broadcast('connection', {
        tab: this.getTabInfo(),
        type: 'join'
      })

      // Request current state from other tabs
      await this.broadcast('connection', {
        tab: this.getTabInfo(),
        type: 'request-state'
      })

      // Set up heartbeat to maintain tab list
      this.startHeartbeat()

      this.emit('enabled')
    } catch (error) {
      throw new Error(`Failed to enable multi-tab sync: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Disable multi-tab sync
   */
  async disable(): Promise<void> {
    if (!this.isEnabled) {
      return
    }

    // Announce disconnection
    if (this.broadcastChannel) {
      await this.broadcast('disconnection', {
        tab: this.getTabInfo(),
        type: 'leave'
      })
    }

    // Clean up
    if (this.broadcastChannel) {
      this.broadcastChannel.close()
      this.broadcastChannel = undefined
    }

    // Clear timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer)
    }
    this.debounceTimers.clear()

    // Clean up compression worker
    if (this.compressionWorker) {
      this.compressionWorker.terminate()
      this.compressionWorker = undefined
    }

    this.isEnabled = false
    this.connectedTabs.clear()
    this.emit('disabled')
  }

  /**
   * Sync file changes across tabs
   */
  async syncFileChange(change: FileSyncChange): Promise<void> {
    if (!this.isEnabled || !this.options.syncFiles) {
      return
    }

    // Add tab ID and hash if not present
    change.tabId = change.tabId || this.tabId
    change.hash = change.hash || this.generateChangeHash(change)

    // Check for conflicts
    if (this.detectConflict(change)) {
      await this.handleConflict(change)
      return
    }

    // Broadcast file change
    await this.debouncedBroadcast('file-change', change, change.path)
  }

  /**
   * Sync cursor position across tabs
   */
  async syncCursor(cursorInfo: CursorSyncInfo): Promise<void> {
    if (!this.isEnabled || !this.options.syncCursor) {
      return
    }

    cursorInfo.tabId = this.tabId

    await this.debouncedBroadcast('cursor-move', cursorInfo, 'cursor')
  }

  /**
   * Sync selection across tabs
   */
  async syncSelection(cursorInfo: CursorSyncInfo): Promise<void> {
    if (!this.isEnabled || !this.options.syncCursor) {
      return
    }

    cursorInfo.tabId = this.tabId

    await this.debouncedBroadcast('selection-change', cursorInfo, 'selection')
  }

  /**
   * Sync state changes across tabs
   */
  async syncStateChange(stateChange: { key: string; value: any; timestamp?: number }): Promise<void> {
    if (!this.isEnabled || !this.options.syncState) {
      return
    }

    const payload = {
      ...stateChange,
      timestamp: stateChange.timestamp || Date.now(),
      tabId: this.tabId
    }

    await this.debouncedBroadcast('state-change', payload, stateChange.key)
  }

  /**
   * Sync terminal output (if enabled)
   */
  async syncTerminalOutput(output: { data: string; type: 'stdout' | 'stderr'; timestamp?: number }): Promise<void> {
    if (!this.isEnabled || !this.options.syncTerminal) {
      return
    }

    const payload = {
      ...output,
      timestamp: output.timestamp || Date.now(),
      tabId: this.tabId
    }

    await this.broadcast('terminal-output', payload)
  }

  /**
   * Sync scroll position
   */
  async syncScroll(scrollInfo: { file?: string; x: number; y: number }): Promise<void> {
    if (!this.isEnabled) {
      return
    }

    const payload = {
      ...scrollInfo,
      tabId: this.tabId,
      timestamp: Date.now()
    }

    await this.debouncedBroadcast('scroll-sync', payload, 'scroll')
  }

  /**
   * Mark tab as active/inactive
   */
  async setTabActive(active: boolean): Promise<void> {
    if (!this.isEnabled) {
      return
    }

    const tab = this.getTabInfo()
    tab.isActive = active
    this.connectedTabs.set(this.tabId, tab)

    await this.broadcast(active ? 'tab-active' : 'tab-inactive', {
      tab,
      timestamp: Date.now()
    })
  }

  /**
   * Get list of connected tabs
   */
  getConnectedTabs(): TabInfo[] {
    return Array.from(this.connectedTabs.values())
  }

  /**
   * Get active tabs
   */
  getActiveTabs(): TabInfo[] {
    return this.getConnectedTabs().filter(tab => tab.isActive)
  }

  /**
   * Check if there are conflicts for a specific file
   */
  hasConflicts(filePath: string): boolean {
    return this.conflictQueue.has(filePath)
  }

  /**
   * Get conflicts for a specific file
   */
  getConflicts(filePath: string): FileSyncChange[] {
    return this.conflictQueue.get(filePath) || []
  }

  /**
   * Resolve conflict manually
   */
  async resolveConflict(filePath: string, resolution: 'accept-local' | 'accept-remote' | 'merge', mergedContent?: string): Promise<void> {
    const conflicts = this.conflictQueue.get(filePath)
    if (!conflicts || conflicts.length === 0) {
      return
    }

    let resolvedChange: FileSyncChange

    switch (resolution) {
      case 'accept-local':
        // Keep local changes, discard remote
        this.conflictQueue.delete(filePath)
        return

      case 'accept-remote':
        // Accept remote changes
        resolvedChange = conflicts[conflicts.length - 1] // Latest remote change
        break

      case 'merge':
        if (!mergedContent) {
          throw new Error('Merged content is required for merge resolution')
        }
        resolvedChange = {
          ...conflicts[0],
          content: mergedContent,
          timestamp: Date.now(),
          hash: this.generateChangeHash({ path: filePath, content: mergedContent } as FileSyncChange)
        }
        break

      default:
        throw new Error(`Unknown conflict resolution: ${resolution}`)
    }

    this.conflictQueue.delete(filePath)

    // Broadcast resolved change
    await this.broadcast('file-change', resolvedChange)
    this.emit('conflict-resolved', { filePath, resolution, change: resolvedChange })
  }

  /**
   * Setup BroadcastChannel message handlers
   */
  private setupMessageHandlers(): void {
    if (!this.broadcastChannel) return

    this.broadcastChannel.addEventListener('message', async (event) => {
      const message: SyncMessage = event.data

      // Ignore messages from this tab
      if (message.tabId === this.tabId) {
        return
      }

      // Deduplication
      if (this.messageDeduplication.has(message.messageId)) {
        return
      }
      this.messageDeduplication.add(message.messageId)

      // Clean up old message IDs (keep last 1000)
      if (this.messageDeduplication.size > 1000) {
        const oldIds = Array.from(this.messageDeduplication).slice(0, 500)
        for (const id of oldIds) {
          this.messageDeduplication.delete(id)
        }
      }

      // Decompress if needed
      let payload = message.payload
      if (message.compressed && this.options.enableCompression) {
        payload = await this.decompress(payload)
      }

      // Handle different message types
      switch (message.type) {
        case 'connection':
          await this.handleConnectionMessage(payload)
          break
        case 'disconnection':
          this.handleDisconnectionMessage(payload)
          break
        case 'file-change':
          this.emit('remote-file-change', payload as FileSyncChange)
          break
        case 'cursor-move':
          this.emit('remote-cursor-move', payload as CursorSyncInfo)
          break
        case 'selection-change':
          this.emit('remote-selection-change', payload as CursorSyncInfo)
          break
        case 'state-change':
          this.emit('remote-state-change', payload)
          break
        case 'terminal-output':
          this.emit('remote-terminal-output', payload)
          break
        case 'scroll-sync':
          this.emit('remote-scroll-sync', payload)
          break
        case 'tab-active':
        case 'tab-inactive':
          this.handleTabActivityMessage(payload)
          break
        case 'conflict':
          this.emit('conflict-detected', payload)
          break
      }
    })
  }

  /**
   * Handle connection messages
   */
  private async handleConnectionMessage(payload: any): Promise<void> {
    const { tab, type } = payload

    switch (type) {
      case 'join':
        this.connectedTabs.set(tab.id, tab)
        this.emit('tab-connected', tab)

        // Send current state to new tab
        await this.broadcast('connection', {
          tab: this.getTabInfo(),
          type: 'state-response',
          state: await this.getCurrentState()
        })
        break

      case 'request-state':
        // Send current state to requesting tab
        await this.broadcast('connection', {
          tab: this.getTabInfo(),
          type: 'state-response',
          state: await this.getCurrentState()
        })
        break

      case 'state-response':
        // Receive state from other tab
        this.emit('state-received', payload.state)
        break
    }
  }

  /**
   * Handle disconnection messages
   */
  private handleDisconnectionMessage(payload: any): void {
    const { tab } = payload
    this.connectedTabs.delete(tab.id)
    this.emit('tab-disconnected', tab)
  }

  /**
   * Handle tab activity messages
   */
  private handleTabActivityMessage(payload: any): void {
    const { tab } = payload
    this.connectedTabs.set(tab.id, tab)
    this.emit('tab-activity-changed', tab)
  }

  /**
   * Broadcast message with debouncing
   */
  private async debouncedBroadcast(type: SyncEventType, payload: any, key: string): Promise<void> {
    // Clear existing timer for this key
    const existingTimer = this.debounceTimers.get(key)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Set new timer
    const timer = setTimeout(async () => {
      await this.broadcast(type, payload)
      this.debounceTimers.delete(key)
    }, this.options.debounceMs || 100)

    this.debounceTimers.set(key, timer)
  }

  /**
   * Broadcast message to all tabs
   */
  private async broadcast(type: SyncEventType, payload: any): Promise<void> {
    if (!this.broadcastChannel) return

    let finalPayload = payload
    let compressed = false

    // Compress large payloads
    const payloadSize = JSON.stringify(payload).length
    if (this.options.enableCompression &&
        payloadSize > (this.options.maxPayloadSize || 1024) &&
        this.compressionWorker) {
      finalPayload = await this.compress(payload)
      compressed = true
    }

    const message: SyncMessage = {
      type,
      payload: finalPayload,
      timestamp: Date.now(),
      tabId: this.tabId,
      sandboxId: this.sandboxId,
      messageId: this.generateMessageId(),
      sequenceNumber: ++this.sequenceNumber,
      compressed
    }

    this.broadcastChannel.postMessage(message)
  }

  /**
   * Detect conflicts in file changes
   */
  private detectConflict(change: FileSyncChange): boolean {
    if (this.options.conflictResolution === 'last-write-wins') {
      return false
    }

    // Check if there are pending changes for this file
    const existingConflicts = this.conflictQueue.get(change.path)
    if (existingConflicts && existingConflicts.length > 0) {
      return true
    }

    // Check if change conflicts with known remote changes
    // This would require maintaining a more sophisticated change history
    return false
  }

  /**
   * Handle file change conflicts
   */
  private async handleConflict(change: FileSyncChange): Promise<void> {
    const existing = this.conflictQueue.get(change.path) || []
    existing.push(change)
    this.conflictQueue.set(change.path, existing)

    this.emit('conflict', {
      file: change.path,
      conflicts: existing,
      newChange: change
    })

    // Auto-resolve if configured
    if (this.options.conflictResolution === 'last-write-wins') {
      await this.resolveConflict(change.path, 'accept-remote')
    } else if (this.options.conflictResolution === 'merge') {
      // Attempt automatic merge (simplified)
      const mergedContent = this.attemptAutoMerge(existing)
      if (mergedContent) {
        await this.resolveConflict(change.path, 'merge', mergedContent)
      }
    }
  }

  /**
   * Attempt automatic merge of conflicting changes
   */
  private attemptAutoMerge(conflicts: FileSyncChange[]): string | null {
    // Simplified merge logic - in practice, this would be more sophisticated
    if (conflicts.length !== 2) return null

    const [local, remote] = conflicts
    if (!local.content || !remote.content) return null

    // For now, just return the remote content
    // In a real implementation, this would use proper merge algorithms
    return remote.content
  }

  /**
   * Generate unique tab ID
   */
  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${this.tabId}_${Math.random().toString(36).substr(2, 6)}`
  }

  /**
   * Generate hash for change detection
   */
  private generateChangeHash(change: FileSyncChange): string {
    const content = `${change.path}:${change.content}:${change.timestamp}`
    // Simple hash - in production, use a proper hash function
    let hash = 0
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return hash.toString(36)
  }

  /**
   * Get current tab info
   */
  private getTabInfo(): TabInfo {
    return {
      id: this.tabId,
      title: typeof document !== 'undefined' ? document.title : undefined,
      lastActivity: Date.now(),
      isActive: typeof document !== 'undefined' ? !document.hidden : true,
      currentFile: undefined, // Would be set by the editor
      scrollPosition: typeof window !== 'undefined' ? { x: window.scrollX, y: window.scrollY } : undefined
    }
  }

  /**
   * Get current application state for sharing
   */
  private async getCurrentState(): Promise<any> {
    return {
      timestamp: Date.now(),
      tabId: this.tabId,
      // Add state collection logic here
      // This would gather current editor state, open files, etc.
    }
  }

  /**
   * Start heartbeat to maintain connected tabs list
   */
  private startHeartbeat(): void {
    setInterval(() => {
      if (this.isEnabled) {
        this.broadcast('connection', {
          tab: this.getTabInfo(),
          type: 'heartbeat'
        })
      }
    }, 30000) // Every 30 seconds
  }

  /**
   * Initialize compression worker
   */
  private initializeCompressionWorker(): void {
    // Create a simple compression worker using built-in compression
    // In a real implementation, this would use a proper compression library
    const workerCode = `
      self.onmessage = function(e) {
        const { type, data } = e.data;

        if (type === 'compress') {
          // Simple compression simulation
          const result = JSON.stringify(data);
          self.postMessage({ type: 'compressed', result });
        } else if (type === 'decompress') {
          // Simple decompression simulation
          const result = JSON.parse(data);
          self.postMessage({ type: 'decompressed', result });
        }
      };
    `

    const blob = new Blob([workerCode], { type: 'application/javascript' })
    this.compressionWorker = new Worker(URL.createObjectURL(blob))
  }

  /**
   * Compress payload
   */
  private async compress(payload: any): Promise<any> {
    return new Promise((resolve) => {
      if (!this.compressionWorker) {
        resolve(payload) // Fallback to uncompressed
        return
      }

      const handler = (e: MessageEvent) => {
        if (e.data.type === 'compressed') {
          this.compressionWorker!.removeEventListener('message', handler)
          resolve(e.data.result)
        }
      }

      this.compressionWorker.addEventListener('message', handler)
      this.compressionWorker.postMessage({ type: 'compress', data: payload })
    })
  }

  /**
   * Decompress payload
   */
  private async decompress(compressedPayload: any): Promise<any> {
    return new Promise((resolve) => {
      if (!this.compressionWorker) {
        resolve(compressedPayload) // Fallback to treating as uncompressed
        return
      }

      const handler = (e: MessageEvent) => {
        if (e.data.type === 'decompressed') {
          this.compressionWorker!.removeEventListener('message', handler)
          resolve(e.data.result)
        }
      }

      this.compressionWorker.addEventListener('message', handler)
      this.compressionWorker.postMessage({ type: 'decompress', data: compressedPayload })
    })
  }

  /**
   * Get sync status
   */
  get enabled(): boolean {
    return this.isEnabled
  }

  get connected(): boolean {
    return this.isEnabled && !!this.broadcastChannel
  }

  get tabCount(): number {
    return this.connectedTabs.size + 1 // +1 for current tab
  }
}